import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { FeatureFlag } from '@polyai/common/types/resources/featureFlags';

import api from 'api';
import type { HTTPError } from 'api/error';
import type { FeaturesResponse } from 'api/internalResources/featureFlag';
import { isLocalDev, setLocalFeatureFlag } from 'lib/localFeatureFlagOverrides';
import { useToast } from '../useToast';
import { useIsFeatureOn } from './useIsFeatureOn';

const DEFAULT_FEATURES = [] as FeaturesResponse;

// eslint-disable-next-line import/no-default-export
export default function useFeatureSwitch() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { isFeatureOn, features, isLoadingFeatures, didFeaturesError } =
    useIsFeatureOn();

  const { mutate: createFeature, isLoading: isLoadingCreate } = useMutation(
    api.createFeature,
    {
      retry: false,
      onSuccess: () => {
        queryClient
          .invalidateQueries(['features'])
          .catch(e =>
            console.error('Error occurred. Could not invalidate queries', e),
          );
      },
      onError: (error: HTTPError, { name }) => {
        let message = error.message;
        if (features?.some(feature => feature.name === name)) {
          message = `Feature named "${name}" already exists.`;
        }
        toast.error({
          title: 'Failed to create new feature flag',
          description: message,
        });
      },
    },
  );

  const { mutate: updateFeatureApi, isLoading: isLoadingUpdateApi } =
    useMutation(api.updateFeature, {
      retry: false,
      onSuccess: () => {
        queryClient
          .invalidateQueries(['features'])
          .catch(e =>
            console.error('Error occurred. Could not invalidate queries', e),
          );
      },
      onError: (error: HTTPError) => {
        toast.error({
          title: 'Failed to update feature flag',
          description: error.message,
        });
      },
    });

  const {
    mutate: updateLocalFeatureFlag,
    isLoading: isLoadingUpdateLocalFeatureFlag,
  } = useMutation(
    async (featureFlag: FeatureFlag) => {
      const success = await setLocalFeatureFlag(
        featureFlag.name,
        featureFlag.enabled,
      );
      if (!success) {
        throw new Error('Failed to update local feature flag');
      }
      return featureFlag;
    },
    {
      retry: false,
      onSuccess: () => {
        queryClient
          .invalidateQueries(['local-feature-flags'])
          .catch(e =>
            console.error(
              'Error occurred. Could not invalidate local feature flags',
              e,
            ),
          );
      },
      onError: (error: Error) => {
        toast.error({
          title: 'Failed to update local feature flag',
          description: error.message,
        });
      },
    },
  );

  const updateFeature = isLocalDev()
    ? updateLocalFeatureFlag
    : updateFeatureApi;
  const isLoadingUpdate = isLocalDev()
    ? isLoadingUpdateLocalFeatureFlag
    : isLoadingUpdateApi;

  const { mutate: deleteFeature, isLoading: isLoadingDelete } = useMutation(
    api.deleteFeature,
    {
      retry: false,
      onSuccess: (_, { name }) => {
        queryClient.setQueryData(
          ['features'],
          features?.filter(feature => feature.name !== name),
        );
      },
      onError: (error: Error) => {
        toast.error({
          title: 'Failed to delete feature flag',
          description: error.message,
        });
      },
    },
  );

  return {
    features: features ?? DEFAULT_FEATURES,
    isLoadingFeatures,
    didFeaturesError,
    createFeature,
    updateFeature,
    deleteFeature,
    isFeatureOn,
    isLoadingCreate,
    isLoadingUpdate,
    isLoadingDelete,
  };
}
